import { Grid, Card, CardContent, Box, Typography, Link } from '@mui/material';
import ReadingThumbnail from './ReadingThumbnail';

interface Reading {
    _id: string;
    question?: string;
    interpretation: {
        metadata?: {
            type?: string;
            setup?: string | number | number[];
        };
        detailed_reading?: {
            final_conclusion?: string;
        };
    };
    createdAt?: any;
}

interface ReadingItemProps {
    reading: Reading;
    index: number;
    totalReadings: number;
}

const formatDate = (timestamp: string | number | undefined) => {
    if (!timestamp) {
        return 'Date not available';
    }

    try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
            return 'Date not available';
        }

        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 7) {
            return date.toLocaleString('en-US', {
                weekday: 'long',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            });
        } else if (date.getFullYear() === now.getFullYear()) {
            return date.toLocaleString('en-US', {
                day: 'numeric',
                month: 'long',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            });
        } else {
            return date.toLocaleString('en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            });
        }
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'Date not available';
    }
};

export default function ReadingItem({ reading, index, totalReadings }: ReadingItemProps) {
    try {
        if (!reading || !reading._id) {
            console.warn('Invalid reading data:', reading);
            return null;
        }
        console.log('reading?.interpretation?.metadata?.type', reading);
        return (
            <Grid item xs={12} sx={{ marginBottom: 0 }}>
                <Link href={`/reading/${reading._id}`} underline="none">
                    <Card sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: (theme) => theme.palette.background.paper,
                            transition: 'background-color 0.3s ease-in-out'
                        }
                    }}>
                        <CardContent sx={{ padding: 1 }}>
                            <Box display="flex" alignItems="center">
                                <Box mr={2} flexShrink={0}>
                                    <ReadingThumbnail
                                        type={reading?.interpretation?.metadata?.selectedReading?.type}
                                        setup={(() => {
                                            const setup = reading?.interpretation?.metadata?.setup;
                                            if (!setup) return undefined;

                                            // Handle different data types
                                            if (typeof setup === 'string') {
                                                return setup.split(',').map((item: string) => Number(item)).filter((num: number) => !isNaN(num));
                                            } else if (Array.isArray(setup)) {
                                                return setup.map((item: any) => Number(item)).filter((num: number) => !isNaN(num));
                                            } else if (typeof setup === 'number') {
                                                return [setup];
                                            }

                                            return undefined;
                                        })()}
                                        width="80px"
                                        height="80px"
                                    />
                                </Box>
                                <Box flexGrow={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(reading?.createdAt)}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mt: 0.5,
                                            mb: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {reading?.question || 'No question provided'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            overflow: 'hidden',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {reading?.interpretation?.detailed_reading?.final_conclusion || 'No conclusion available'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Link>
                {index < totalReadings - 1 && (
                    <Box
                        sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '100%',
                            mt: 0
                        }}
                    />
                )}
            </Grid>
        );
    } catch (error) {
        console.error('Error rendering ReadingItem:', error, reading);
        return (
            <Grid item xs={12} sx={{ marginBottom: 0 }}>
                <Card sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                    <CardContent sx={{ padding: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Error loading reading
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        );
    }
} 